﻿import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  ALL_TRIBUTE_VIDEO_EXPORTS_QUERY_KEY,
  CANCEL_EXPORT_RENDERING,
  GET_TRIBUTE_VIDEO_EXPORT_STATUS_QUERY_KEY,
  TRIBUTE_VIDEO_EXPORT_API_URL
} from '@/constants';
import { TributeVideoExportStatus } from '@/enums';
import { apiUrl, getQueryParams } from '@/helpers';
import { ErrorResponse, Options } from '@/types';

const DEFAULT_SORTING_FIELD = 'CreatedAt';
const DEFAULT_SORTING_ASC = false;

export const tributeVideoExportEndpoints = {
  getAllTributeVideoExports: (
    pageSize: number,
    cursor: number,
    searchQuery?: string,
    status?: TributeVideoExportStatus,
    sortBy = DEFAULT_SORTING_FIELD,
    sort = DEFAULT_SORTING_ASC
  ) => {
    const params: Record<string, number | string | boolean | TributeVideoExportStatus> = {
      'paging.PageSize': pageSize,
      'paging.Cursor': cursor,
      'sorting.Field': sortBy,
      'sorting.Asc': sort
    };

    if (searchQuery) {
      params['search.Text'] = searchQuery;
    }

    if (status !== undefined) {
      params['filter.Status'] = status;
    }

    return axios.get(`${apiUrl()}${TRIBUTE_VIDEO_EXPORT_API_URL}${getQueryParams(params)}`);
  },
  getTributeVideoExportStatus: (id: number) =>
    axios.get(`${apiUrl()}${TRIBUTE_VIDEO_EXPORT_API_URL}/exportstatus/${id}`),
  cancelVideoRendering: (id: number) =>
    axios.get(`${apiUrl()}${TRIBUTE_VIDEO_EXPORT_API_URL}/${id}/cancel`)
};

export const useGetTributeVideoExportStatus = (id: number, options?: Options) => {
  return useQuery(
    [GET_TRIBUTE_VIDEO_EXPORT_STATUS_QUERY_KEY],
    () => tributeVideoExportEndpoints.getTributeVideoExportStatus(id),
    {
      enabled: !!id,
      refetchInterval: options?.refetchInterval,
      cacheTime: 0,
      onSuccess: (response) => {
        options?.onSuccess(response);
      },
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useGetAllTributeVideoExports = (
  pageSize: number,
  searchQuery?: string,
  status?: TributeVideoExportStatus,
  userId?: number
) => {
  return useInfiniteQuery(
    [ALL_TRIBUTE_VIDEO_EXPORTS_QUERY_KEY, searchQuery, status, userId],
    ({ pageParam = 0 }) =>
      tributeVideoExportEndpoints.getAllTributeVideoExports(
        pageSize,
        pageParam,
        searchQuery,
        status
      ),
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      }
    }
  );
};

export const useCancelVideoRendering = (queryClient: QueryClient, options: Options) => {
  return useMutation(tributeVideoExportEndpoints.cancelVideoRendering, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([CANCEL_EXPORT_RENDERING]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
